import { useEffect, useState } from "react";
import { router } from "@inertiajs/react";
import { useQueryClient } from "@tanstack/react-query";
import { Body } from "@/sprout/typography";
import {
  PrimaryButton,
  PrimaryButtonLink,
  SecondaryButtonLink,
  TertiaryButton,
} from "@sprout/buttons";
import type { GhrTask } from "@/types/task";
import useTranslation from "@hooks/use_translation";
import useTaskStatusText from "@hooks/use_task_status_text";
import useTasks from "@hooks/use_tasks";
import { useVerification } from "@hooks/use_verification";
import GHR_TASK_CONFIG from "@/config/ghr_task_config";
import ClearVerificationModal from "@components/ClearVerificationModal";

const URGENT_LIMIT_MS = 3 * 24 * 60 * 60 * 1000;

const IdentityVerificationAction = ({ task }: { task: GhrTask }) => {
  const { t } = useTranslation("clear_verification");
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();
  const { isRefetching } = useTasks();

  const { verifyWithClear } = useVerification({
    taskId: task.taskable_id,
    onSuccess: () => {
      setShowModal(false);
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  useEffect(() => {
    if (task.auto_open_modal && !isRefetching && task.status !== "awaiting_user_input") {
      setShowModal(true);
    }
  }, [task.auto_open_modal, isRefetching, task.status]);

  const handleVerify = async () => {
    setShowModal(false);
    await verifyWithClear();
  };

  return (
    <>
      {task.status === "processing_data" ? (
        <PrimaryButton size="medium" disabled>
          {t("status.processing_data.label")}
        </PrimaryButton>
      ) : task.status === "awaiting_user_input" && task.verification_url ? (
        <PrimaryButtonLink size="medium" href={task.verification_url} external>
          {t("cta.continue_verification")}
        </PrimaryButtonLink>
      ) : (
        <PrimaryButton size="medium" onClick={() => setShowModal(true)}>
          {t("cta.verify_identity")}
        </PrimaryButton>
      )}
      <ClearVerificationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onVerify={handleVerify}
        companyName={task.company_name}
      />
    </>
  );
};

const ApplicationTaskBanner = ({
  task,
  jobSeekersTasksEnabled,
}: {
  task: GhrTask;
  jobSeekersTasksEnabled: boolean;
}) => {
  const { t } = useTranslation("tasks");
  const queryClient = useQueryClient();
  const statusText = useTaskStatusText(task);
  const config = GHR_TASK_CONFIG[task.taskable_type];
  const isIdentityVerification = task.taskable_type === "IdentityVerificationTask";

  if (!config || (!task.url && !isIdentityVerification)) {
    return null;
  }

  const Icon = config.icon;
  const title = t(config.titleKey);

  if (!jobSeekersTasksEnabled) {
    return (
      <div className="application-task-banner w-full bg-white border border-gray-700 rounded-lg px-6 py-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <div className="flex items-center justify-center bg-gray-500 rounded w-[30px] h-[30px]">
                <Icon size="sm" />
              </div>
              <Body className="!text-xs !ml-1" metadata>
                {statusText}
              </Body>
            </div>
            <Body medium>{title}</Body>
          </div>
          <div className="md:shrink-0">
            {task.url && (
              <SecondaryButtonLink href={task.url} size="medium" external>
                {t(config.actionTextKey)}
              </SecondaryButtonLink>
            )}
          </div>
        </div>
      </div>
    );
  }

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: ["tasks"] });
  const handleDismiss = () =>
    router.post(
      `/tasks/${task.id}/dismiss`,
      {},
      { preserveScroll: true, preserveState: true, onSuccess: invalidateQuery },
    );

  const isOverdue = Date.now() - new Date(task.created_at).getTime() > URGENT_LIMIT_MS;
  const backgroundClass = isOverdue ? "bg-[var(--extra-light-red)]" : "bg-pale-blue";
  const iconBackgroundClass = isOverdue ? "bg-poppy-e50" : "bg-iris-e50";

  return (
    <div className={`application-task-banner w-full ${backgroundClass} rounded-lg px-6 py-4`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <div
              className={`flex items-center justify-center ${iconBackgroundClass} rounded w-[30px] h-[30px]`}
            >
              <Icon size="sm" />
            </div>
            <Body className="!text-xs !ml-1" metadata>
              {statusText}
            </Body>
          </div>
          <Body medium>{title}</Body>
        </div>
        <div className="flex items-center gap-4 md:shrink-0">
          <TertiaryButton onClick={handleDismiss}>{t("common.dismiss")}</TertiaryButton>
          {isIdentityVerification ? (
            <IdentityVerificationAction task={task} />
          ) : (
            task.url && (
              <PrimaryButtonLink href={task.url} size="medium" external onClick={invalidateQuery}>
                {t(config.actionTextKey)}
              </PrimaryButtonLink>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationTaskBanner;
